import React from 'react';

import { PAYMENT_METHOD, calcValue, getFullName, isDefined, isEmpty } from '@nfctron/nexus-core';
import {
	Markdown,
	UiAlert,
	UiAlertDescription,
	UiAlertHeader,
	UiAlertTitle,
	UiBadge,
	UiButton,
	UiDrawer,
	UiDrawerClose,
	UiDrawerContent,
	UiDrawerDescription,
	UiDrawerFooter,
	UiDrawerHeader,
	UiDrawerTitle,
	UiDrawerTrigger,
	cn,
	useContextRequired,
	useFormat,
} from '@nfctron/nexus-react';
import { McClickToPayBlackLogo, McClickToPayWhiteLogo, NfctronTextLogo } from '@nfctron/nexus-react-svg';

import { ActionIcon, Loader, Text } from '@mantine/core';
import { CircleAlertIcon, LeafIcon, PenLineIcon, StarIcon } from 'lucide-react';
import { Swiper, SwiperSlide } from 'swiper/react';

import { getEventTypeSensitiveComponentDisplayName } from '@root/pages/event/[organizer]/[event]/_resources/helpers/common';

import { AccountSheet } from '@root/lib/components/AccountSheet/AccountSheet';
import { Address } from '@root/lib/components/Address/Address';
import { UiRadioGroup, UiRadioGroupItem } from '@root/lib/components/ui/radio-group';
import { APP_I18N_NAMESPACE } from '@root/lib/constants/common';
import { useTranslate } from '@root/lib/hooks/useTranslation';
import { type AtomComponent } from '@root/lib/types/components';

import { EventCartPageContext } from '../../components/CartContext';
import { TicketCardCarted } from '../../components/Ticket/TicketCardCarted';
import { CartStorePaymentOptionsSlice, cartStoreSelectors, useCartStore } from '../../stores/cartStore';

/**
 * Event/Cart/CheckoutViewContent atom component
 * @author filipditrich <ditrich@nfctron.com>
 * @constructor
 */
export const CheckoutViewContentAtom: AtomComponent = () => {
	const { event } = useContextRequired(EventCartPageContext);
	const { t } = useTranslate(APP_I18N_NAMESPACE.MAIN, CheckoutViewContentAtom.displayName);
	const { t: commonT, tor: commonTor } = useTranslate(APP_I18N_NAMESPACE.COMMON);
	const cartConfig = useCartStore((s) => s.cartConfig);
	const { t: typeSensitiveT } = useTranslate(
		APP_I18N_NAMESPACE.MAIN,
		getEventTypeSensitiveComponentDisplayName(CheckoutViewContentAtom.displayName, cartConfig.eventTypeId),
	);
	const format = useFormat();
	const activePaymentOptionId = useCartStore((s) => s.activePaymentOptionId);
	const contactDetails = useCartStore((s) => s.contactDetails);
	const isContactDetailsSheetOpen = useCartStore((s) => s.isContactDetailsSheetOpen);
	const setContactDetailsSheetOpen = useCartStore((s) => s.setContactDetailsSheetOpen);
	const setActivePaymentOptionId = useCartStore((s) => s.setActivePaymentOptionId);
	const updateContactDetails = useCartStore((s) => s.updateContactDetails);
	const getMaximusProductsForPaymentMethod = useCartStore((s) => s.getMaximusProductsForPaymentMethod);
	const canCloseContactDetailsSheet = useCartStore((s) => s.canCloseContactDetailsSheet());
	const paymentOptionsListed = useCartStore(cartStoreSelectors.listPaymentOptions);
	const cartedTickets = useCartStore((s) => s.cartedTickets);
	const cartTotal = useCartStore((s) => s.getCartTotal());
	const cartedTicketTypes = useCartStore((s) => s.getCartedTicketTypes(), 'deep');
	const isClickToPayPromoActive = cartedTicketTypes.some((tt) => tt.extraData.promotionCampaign?.type === 'mc-click-to-pay');

	return (
		<React.Fragment>
			{/* ticket listing section */}
			<section className="flex flex-col gap-2">
				{/* heading */}
				<Text fw={700} size="md" c="dimmed">
					{typeSensitiveT('ticketsHeading')}
				</Text>
				{/* tickets */}
				<Swiper className="w-full" spaceBetween={16} slidesPerView={cartedTickets.length > 1 ? 1.125 : 1}>
					{cartedTickets.map((ticket) => (
						<SwiperSlide key={ticket.cartTicketId} className="flex flex-col">
							<TicketCardCarted className="h-full" cartTicketId={ticket.cartTicketId} />
						</SwiperSlide>
					))}
				</Swiper>
			</section>

			{/* contact details */}
			<section className="flex flex-col gap-2">
				{/* heading */}
				<header className="flex items-center justify-between gap-3">
					<Text fw={700} size="md" c="dimmed">
						{t('contactDetailsHeading')}
					</Text>
					<ActionIcon size="lg" variant="subtle" onClick={() => setContactDetailsSheetOpen(true)}>
						<PenLineIcon size={19} color="gray" />
					</ActionIcon>
				</header>
				{/* details */}
				{!isEmpty(contactDetails.email) ? (
					<div className="grid w-full grid-cols-2 items-start gap-3 rounded-xl border border-gray-200 bg-gray-50 p-4">
						<div>
							{/* full name */}
							<span className="text-lucky-point-500 block text-lg font-bold">{getFullName(contactDetails)}</span>
							{/* email */}
							<span className="block text-base text-zinc-600">{contactDetails.email}</span>
							{/* phone */}
							{!isEmpty(contactDetails.phone) && <span className="block text-base text-zinc-600">{contactDetails.phone}</span>}
						</div>

						{/* billing info */}
						<div className="text-base text-zinc-600">
							{/* legal name */}
							{!isEmpty(contactDetails.legalName) && <span className="text-lucky-point-500 block text-lg font-bold">{contactDetails.legalName}</span>}
							{/* regNumber */}
							{!isEmpty(contactDetails.regNumber) && (
								<span className="block">
									<span>{t('regNumber.label')}</span>: <span>{contactDetails.regNumber}</span>
								</span>
							)}
							{/* vatId */}
							{!isEmpty(contactDetails.vatId) && (
								<span className="block">
									<span>{t('vatId.label')}</span>: <span>{contactDetails.vatId}</span>
								</span>
							)}
							{/* address */}
							<Address className="text-base text-zinc-600 not-italic" address={contactDetails} />
						</div>
					</div>
				) : (
					<div className="flex min-h-[150px] flex-col items-center justify-center gap-3 rounded-xl border border-gray-200 bg-gray-50 p-4">
						<UiButton variant="primary" onClick={() => setContactDetailsSheetOpen(true)} leftSection={<PenLineIcon />}>
							{t('contactDetailsHeading')}
						</UiButton>
					</div>
				)}
			</section>

			{/* payment methods section */}
			{/* FIXME */}
			<section className={cn(['flex flex-col gap-2', cartTotal.total === 0 && 'hidden'])}>
				{/* heading */}
				<Text fw={700} size="md" c="dimmed">
					{t('paymentMethodsHeading')}
				</Text>

				{/* payment methods */}
				<div className="flex flex-col gap-3 rounded-xl">
					<UiRadioGroup
						value={activePaymentOptionId?.toString()}
						onValueChange={(value) => {
							const paymentOption = paymentOptionsListed.find((po) => po.paymentOptionId.toString() === value);
							setActivePaymentOptionId({ paymentOptionId: paymentOption?.paymentOptionId ?? null });
						}}
					>
						{paymentOptionsListed.map((option) => {
							const label = commonT(`constants.paymentOption.${option.paymentMethodId}.text`);
							const description = commonTor(`constants.paymentOption.${option.paymentOptionId}.description`);
							const isActive = activePaymentOptionId === option.paymentOptionId;
							const badgeNode = calcValue(() => {
								switch (option.paymentOptionId) {
									case CartStorePaymentOptionsSlice.PAYMENT_OPTION_ID.BANK_TRANSFER_EUR:
									case CartStorePaymentOptionsSlice.PAYMENT_OPTION_ID.BANK_TRANSFER_CZK: {
										return (
											<UiBadge variant="filled" color={isActive ? 'purple-heart' : 'amber'} size="sm" rounded="full" leftSection={<StarIcon />}>
												{t('paymentMethods.favoriteTag.text')}
											</UiBadge>
										);
									}
									case CartStorePaymentOptionsSlice.PAYMENT_OPTION_ID.CLICK_TO_PAY: {
										return (
											<UiBadge variant="filled" color="black" rounded="full" size="sm">
												{t('paymentMethods.newTag.text')}
											</UiBadge>
										);
									}
									default: {
										return null;
									}
								}
							});
							const alertNode = calcValue(() => {
								switch (option.paymentOptionId) {
									case CartStorePaymentOptionsSlice.PAYMENT_OPTION_ID.BANK_TRANSFER_EUR:
									case CartStorePaymentOptionsSlice.PAYMENT_OPTION_ID.BANK_TRANSFER_CZK: {
										/** show EUR banner only if it is only bank transfer method available (otherwise it will be shown by the CZE transfer) */
										if (option.paymentOptionId !== CartStorePaymentOptionsSlice.PAYMENT_OPTION_ID.BANK_TRANSFER_CZK) {
											const hasOtherBankTransfer =
												paymentOptionsListed.findIndex(
													(pm) => pm.paymentOptionId !== option.paymentOptionId && pm.paymentMethodId === PAYMENT_METHOD.BANK_TRANSFER,
												) === -1;
											if (!hasOtherBankTransfer) return null;
										}
										return (
											<UiAlert variant="filled" color="green">
												<UiAlertHeader>
													<LeafIcon className="my-1 hidden size-4 shrink-0 md:block" />
													<UiAlertTitle className="text-base font-semibold">{t('bankTransferEcoAlert.title')}</UiAlertTitle>
												</UiAlertHeader>
												<UiAlertDescription className="block pl-0 text-base opacity-100">{t('bankTransferEcoAlert.text')}</UiAlertDescription>
												<UiAlertDescription className="block pl-0 text-sm text-white/75 opacity-100">
													{t('bankTransferEcoAlert.disclaimer')}
												</UiAlertDescription>
											</UiAlert>
										);
									}
									/** paygate down alert */
									case CartStorePaymentOptionsSlice.PAYMENT_OPTION_ID.CLICK_TO_PAY: {
										/** TODO: make flaggable */
										/* return (
											<UiAlert variant="subtle" color="pink">
												<UiAlertHeader>
													<CircleAlertIcon className="size-4" />
													<UiAlertTitle>{t('payGateDownAlert.title')}</UiAlertTitle>
												</UiAlertHeader>
												<UiAlertDescription>{t('payGateDownAlert.text')}</UiAlertDescription>
											</UiAlert>
										); */

										if (!isClickToPayPromoActive) return null;
										return (
											<UiAlert variant="filled" color="unstyled" className="border-transparent bg-black text-white">
												<UiAlertHeader>
													<div className="flex items-center">
														<McClickToPayWhiteLogo className="mr-2 inline h-5 w-14" />
														<UiAlertTitle className="text-base font-semibold">
															{cartTotal.originalTotal > cartTotal.total
																? t('clickToPayBanner.withPriceTitle', {
																		formattedPrice: format.price(cartTotal.originalTotal - cartTotal.total, cartConfig.currencyId),
																	})
																: t('clickToPayBanner.title')}
														</UiAlertTitle>
													</div>
												</UiAlertHeader>
												<UiAlertDescription className="pl-0 opacity-100">
													<Markdown
														className="text-base"
														componentProps={{
															ul: { className: 'my-0' },
														}}
													>
														{t('clickToPayBanner.textMd', {
															eventName: event.namePub,
														})}
													</Markdown>
													<UiDrawer>
														<UiDrawerTrigger asChild>
															<UiButton size="xs" variant="secondary" className="mt-2">
																{t('clickToPayBanner.infoButton')}
															</UiButton>
														</UiDrawerTrigger>
														<UiDrawerContent className="mx-auto max-w-screen-sm">
															<UiDrawerHeader className="flex items-start justify-between gap-3 text-left">
																<div className="flex flex-col gap-2">
																	<div className="mb-6 flex items-center justify-center gap-5 rounded-md border border-zinc-200 bg-zinc-50 px-4 py-16 sm:gap-0">
																		<McClickToPayBlackLogo className="h-8 w-[100px] sm:mr-[-17px] sm:w-[200px]" />
																		<div className="h-8 w-[1px] bg-zinc-200" />
																		<NfctronTextLogo className="h-7 w-[200px]" />
																	</div>
																	<UiDrawerTitle className="mb-4">
																		<Text fw={600}>
																			{cartTotal.originalTotal > cartTotal.total
																				? t('clickToPayBanner.withPriceTitle', {
																						formattedPrice: format.price(cartTotal.originalTotal - cartTotal.total, cartConfig.currencyId),
																					})
																				: t('clickToPayBanner.title')}
																		</Text>
																	</UiDrawerTitle>
																	<UiDrawerDescription>
																		<Markdown
																			className="text-base text-zinc-900"
																			componentProps={{
																				ul: { className: 'my-0' },
																			}}
																		>
																			{cartTotal.originalTotal > cartTotal.total
																				? t('clickToPayBannerSheet.withPriceDescriptionMd', {
																						formattedPrice: format.price(cartTotal.originalTotal - cartTotal.total, cartConfig.currencyId),
																						eventName: event.namePub,
																					})
																				: t('clickToPayBannerSheet.descriptionMd', {
																						eventName: event.namePub,
																					})}
																		</Markdown>
																	</UiDrawerDescription>
																</div>
															</UiDrawerHeader>

															<UiDrawerFooter>
																<UiDrawerClose asChild>
																	<UiButton size="lg" variant="primary">
																		{t('clickToPayBannerSheet.closeLabel')}
																	</UiButton>
																</UiDrawerClose>
															</UiDrawerFooter>
														</UiDrawerContent>
													</UiDrawer>
												</UiAlertDescription>
											</UiAlert>
										);
									}
									default: {
										return null;
									}
								}
							});
							const autoFees = getMaximusProductsForPaymentMethod({ paymentMethodId: option.paymentMethodId })
								.filter((mp) => mp.minQuantity > 0)
								.reduce((acc, curr) => acc + curr.price, 0);
							return (
								<React.Fragment key={option.paymentOptionId}>
									{/* alert node */}
									{isDefined(alertNode) && alertNode}
									{/* radio option */}
									<div>
										<UiRadioGroupItem
											id={option.paymentOptionId.toString()}
											value={option.paymentOptionId.toString()}
											disabled={option.disabled}
											className="peer sr-only"
										/>
										<label
											htmlFor={option.paymentOptionId.toString()}
											data-checked={isActive}
											className={cn(
												'group flex cursor-pointer items-center rounded-lg border border-zinc-200 px-4 py-3 text-gray-900 transition-all ease-in-out',
												'hover:border-zinc-300 hover:bg-zinc-50',
												'peer-data-[state=checked]:border-lucky-point-500 peer-data-[state=checked]:bg-lucky-point-100/15 peer-data-[state=checked]:text-lucky-point-500',
												option.disabled && 'cursor-not-allowed opacity-50',
											)}
										>
											<div className="flex flex-1 items-center space-x-3 md:space-x-4">
												{/* radio icon */}
												{isActive ? (
													<div className="bg-lucky-point-500 grid size-5 place-items-center rounded-full">
														<div className="size-2 rounded-full bg-white" />
													</div>
												) : (
													<div className="grid size-5 place-items-center rounded-full border border-zinc-200 bg-zinc-50 transition-all ease-in-out group-hover:border-zinc-300" />
												)}

												{/* payment method icon */}
												{isDefined(option.Icon) && (
													<div className="flex size-8 items-center justify-center rounded-full border border-zinc-100 bg-zinc-50 md:size-10">
														<option.Icon className={cn('group-data-[checked=true]:text-lucky-point-500 size-6 text-zinc-700 md:size-7')} />
													</div>
												)}
												{/* labels */}
												<div className="flex flex-1 flex-col items-start gap-0.5">
													{/* badge */}
													{isDefined(badgeNode) && badgeNode}
													{/* label */}
													<p className="text-base group-data-[checked=true]:font-semibold md:text-lg">{label}</p>
													{/* description */}
													{!isEmpty(description) && <p className="text-sm opacity-85">{description}</p>}
												</div>
											</div>
											{/* right section */}
											<div className="ml-4 flex flex-col items-end justify-between gap-1">
												{/* automatic fees */}
												{autoFees > 0 ? (
													<span className="text-sm group-data-[checked=true]:font-medium">{`${format.price(autoFees, cartConfig.currencyId)}`}</span>
												) : (
													<span className="text-sm uppercase group-data-[checked=true]:font-medium">{t('noFee.label')}</span>
												)}
											</div>
										</label>
									</div>
								</React.Fragment>
							);
						})}
					</UiRadioGroup>
				</div>
			</section>

			{/* account sheet */}
			<AccountSheet
				useAs="register"
				type="cart"
				registerForm={{
					defaultValues: contactDetails ?? undefined,
				}}
				open={isContactDetailsSheetOpen}
				canClose={canCloseContactDetailsSheet}
				onClose={() => setContactDetailsSheetOpen(false)}
				onLogin={() => setContactDetailsSheetOpen(false)}
				onRegister={(values) => {
					updateContactDetails({ ...values, ...values.billingInfo });
					setContactDetailsSheetOpen(false);
				}}
			/>
		</React.Fragment>
	);
};

/**
 * Event/Cart/CheckoutViewContent atom display name
 */
CheckoutViewContentAtom.displayName = 'Event/Cart/CheckoutViewContentAtom';
